import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';

export default function TickerWatchlist() {
  const [configured, setConfigured] = useState(null);
  const [keyError, setKeyError] = useState(false);
  const [quotes, setQuotes] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [tickerInput, setTickerInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const data = await api.getTickers();
      setConfigured(data.configured);
      setKeyError(!!data.keyError);
      setQuotes(data.quotes);
    } catch (err) {
      console.error(err);
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveKey = async (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.saveFinnhubKey(keyInput.trim());
      setKeyInput('');
      setShowKeyForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    if (!window.confirm('Remove your Finnhub API key? This will stop all ticker price checks.')) return;
    try {
      await api.deleteFinnhubKey();
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const addTicker = async (e) => {
    e.preventDefault();
    if (!tickerInput.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.addTicker(tickerInput.trim());
      setTickerInput('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeTicker = async (ticker) => {
    try {
      await api.removeTicker(ticker);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleShowOnOverview = async (ticker, current) => {
    try {
      await api.toggleTickerOnOverview(ticker, !current);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  if (configured === null && loadFailed) {
    return (
      <div style={styles.panel}>
        <p style={styles.empty}>Couldn't load ticker data. <button style={styles.retryLink} onClick={load}>Retry</button></p>
      </div>
    );
  }

  if (configured === null) {
    return <div style={styles.panel}><p style={styles.empty}>Loading...</p></div>;
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>📊 Ticker Watchlist</h2>
        {configured && (
          <button style={styles.settingsBtn} onClick={() => setShowKeyForm((s) => !s)}>
            {showKeyForm ? 'Close' : '⚙ Finnhub Key'}
          </button>
        )}
      </div>

      {(!configured || showKeyForm) && (
        <form onSubmit={saveKey} style={styles.keyForm}>
          <p style={styles.helpText}>
            {configured
              ? 'Update your Finnhub API key.'
              : keyError
                ? "Your saved Finnhub API key can't be read anymore - this can happen after a security update on the server. Please re-enter it below to get ticker prices working again."
                : "Add your own free Finnhub API key to track stock prices. Get one at "}
            {!configured && !keyError && (
              <a href="https://finnhub.io/register" target="_blank" rel="noopener noreferrer" style={styles.link}>
                finnhub.io/register
              </a>
            )}
          </p>
          <div style={styles.keyRow}>
            <input
              style={styles.keyInput}
              type="password"
              placeholder="Paste your Finnhub API key"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
            />
            <button style={styles.saveBtn} type="submit" disabled={saving || !keyInput.trim()}>
              {saving ? '...' : 'Save'}
            </button>
          </div>
          {configured && (
            <button type="button" style={styles.removeKeyBtn} onClick={removeKey}>
              Remove key
            </button>
          )}
        </form>
      )}

      {configured && (
        <>
          <form onSubmit={addTicker} style={styles.addRow}>
            <input
              style={styles.tickerInput}
              placeholder="Add a ticker (e.g. TSLA)"
              value={tickerInput}
              onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
            />
            <button style={styles.saveBtn} type="submit" disabled={saving || !tickerInput.trim()}>
              + Add
            </button>
          </form>

          {error && <p style={styles.error}>{error}</p>}

          {quotes && quotes.length > 0 && (
            <p style={styles.helpText}>⭐ Starred tickers show on your Overview page (up to 5, biggest movers first).</p>
          )}

          {!quotes || quotes.length === 0 ? (
            <p style={styles.empty}>No tickers followed yet. Add one above.</p>
          ) : (
            <div style={styles.list}>
              {quotes.map((q) => (
                <TickerRow
                  key={q.ticker}
                  quote={q}
                  onRemove={() => removeTicker(q.ticker)}
                  onToggleOverview={() => toggleShowOnOverview(q.ticker, q.showOnOverview)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TickerRow({ quote, onRemove, onToggleOverview }) {
  const overviewToggle = (
    <button
      style={styles.starBtn}
      onClick={onToggleOverview}
      title={quote.showOnOverview ? 'Showing on Overview - click to hide' : 'Hidden from Overview - click to show'}
    >
      {quote.showOnOverview ? '⭐' : '☆'}
    </button>
  );

  if (!quote.available) {
    return (
      <div style={styles.row}>
        {overviewToggle}
        <span style={styles.ticker}>{quote.ticker}</span>
        <span style={styles.unavailable}>Unavailable</span>
        <button style={styles.removeBtn} onClick={onRemove}>✕</button>
      </div>
    );
  }

  const up = quote.changePct >= 0;
  const color = up ? '#00d4aa' : '#ef4444';

  return (
    <div style={styles.row}>
      {overviewToggle}
      <span style={styles.ticker}>{quote.ticker}</span>
      <span style={styles.price}>{fmt.currency(quote.price)}</span>
      <span style={{ ...styles.change, color }}>
        {up ? '▲' : '▼'} {Math.abs(quote.changePct).toFixed(2)}%
      </span>
      <button style={styles.removeBtn} onClick={onRemove}>✕</button>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  settingsBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '0.75rem',
  },
  link: { color: '#00d4aa' },
  keyForm: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  keyRow: { display: 'flex', gap: '0.5rem' },
  keyInput: {
    flex: 1,
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  removeKeyBtn: {
    background: 'transparent',
    border: '1px solid #ef4444',
    color: '#ef4444',
    borderRadius: '6px',
    padding: '5px 12px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginTop: '0.6rem',
  },
  addRow: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  tickerInput: {
    flex: 1,
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'DM Mono', monospace",
    textTransform: 'uppercase',
  },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1.1rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  error: { color: '#ef4444', fontSize: '0.8rem', marginBottom: '0.75rem' },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' },
  retryLink: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.85rem',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: "'Syne', sans-serif",
  },
  list: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.85rem',
  },
  ticker: {
    color: '#fff',
    fontWeight: '700',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    minWidth: '60px',
  },
  price: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
    flex: 1,
  },
  change: {
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  unavailable: {
    color: '#4b5563',
    fontSize: '0.75rem',
    fontStyle: 'italic',
    flex: 1,
  },
  removeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '2px 4px',
  },
  starBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    padding: '0 2px',
    lineHeight: 1,
  },
};
